import { useState, useCallback, useMemo, useEffect } from 'react';
import type { DataProvider } from 'react-admin';

export interface WizardStepDef {
  id: string;
  title: string;
  description?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export interface UseWizardOptions {
  steps: WizardStepDef[];
  initialValues?: Record<string, unknown>;
  dataProvider?: DataProvider;
  resource?: string;
  stepValidators?: Record<string, (values: Record<string, unknown>) => ValidationResult>;
  onSaveDraft?: (values: Record<string, unknown>, stepId: string) => Promise<void>;
  onComplete?: (values: Record<string, unknown>) => Promise<{ id: string }>;
}

function extractErrorMessage(err: unknown, fallback: string): string[] {
  if (err instanceof Error) {
    const msg = err.message;
    if (msg && msg !== 'Session expired') return [msg];
  }
  return [fallback];
}

export function useWizard({ steps, initialValues = {}, dataProvider, resource, stepValidators, onSaveDraft, onComplete }: UseWizardOptions) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stepFeedback, setStepFeedback] = useState<{ type: 'error' | 'success' | 'warning' | 'info'; messages: string[] } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [isBacking, setIsBacking] = useState(false);

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const progress = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < totalSteps) {
      setCurrentStepIndex(index);
      setErrors({});
      setStepFeedback(null);
    }
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setIsBacking(true);
      goToStep(currentStepIndex - 1);
      setTimeout(() => setIsBacking(false), 100);
    }
  }, [currentStepIndex, goToStep]);

  const updateValues = useCallback((newValues: Record<string, unknown>) => {
    setValues(prev => ({ ...prev, ...newValues }));
    setErrors({});
  }, []);

  const setFieldError = useCallback((field: string, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const setFieldErrors = useCallback((newErrors: Record<string, string>) => {
    setErrors(newErrors);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const showFeedback = useCallback((type: 'error' | 'success' | 'warning' | 'info', messages: string[]) => {
    setStepFeedback({ type, messages });
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      if (stepValidators) {
        const validator = stepValidators[currentStep.id];
        if (validator) {
          const result = validator(values);
          if (!result.isValid) {
            setErrors(result.errors);
            showFeedback('error', Object.values(result.errors));
            return;
          }
        }
      }
      goToStep(currentStepIndex + 1);
    }
  }, [currentStepIndex, totalSteps, goToStep, stepValidators, values, showFeedback]);

  const clearFeedback = useCallback(() => {
    setStepFeedback(null);
  }, []);

  const validateStep = useCallback((validator: (values: Record<string, unknown>) => ValidationResult): boolean => {
    const result = validator(values);
    if (!result.isValid) {
      setErrors(result.errors);
      showFeedback('error', Object.values(result.errors));
      return false;
    }
    clearErrors();
    return true;
  }, [values, showFeedback, clearErrors]);

  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      if (onSaveDraft) {
        await onSaveDraft(values, currentStep.id);
      } else if (dataProvider && resource) {
        await dataProvider.create(resource, { data: { ...values, _draft: true, stepId: currentStep.id } });
      }
      showFeedback('success', ['Draft saved successfully']);
    } catch (err) {
      showFeedback('error', extractErrorMessage(err, 'Failed to save draft'));
    } finally {
      setIsSaving(false);
    }
  }, [values, currentStep.id, dataProvider, resource, onSaveDraft, showFeedback]);

  const complete = useCallback(async () => {
    let result: { id: string } | undefined;
    setIsSaving(true);
    try {
      if (onComplete) {
        result = await onComplete(values);
      } else if (dataProvider && resource) {
        const created = await dataProvider.create(resource, { data: values });
        result = { id: created.data.id };
      }
      if (result) {
        setCreatedId(result.id);
      }
      setIsCompleted(true);
    } catch (err) {
      showFeedback('error', extractErrorMessage(err, 'Failed to complete wizard'));
    } finally {
      setIsSaving(false);
    }
  }, [values, dataProvider, resource, onComplete, showFeedback]);

  const reset = useCallback(() => {
    setCurrentStepIndex(0);
    setValues(initialValues);
    setErrors({});
    setStepFeedback(null);
    setIsCompleted(false);
    setCreatedId(null);
  }, [initialValues]);

  return useMemo(() => ({
    currentStep: currentStep as WizardStepDef,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    isBacking,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    goToStep,
    nextStep,
    prevStep,
    updateValues,
    setFieldError,
    setFieldErrors,
    clearErrors,
    showFeedback,
    clearFeedback,
    validateStep,
    saveDraft,
    complete,
    reset,
  }), [
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep, isSaving, isCompleted, isBacking, progress,
    values, errors, stepFeedback, createdId,
    goToStep, nextStep, prevStep, updateValues, setFieldError, setFieldErrors, clearErrors,
    showFeedback, clearFeedback, validateStep, saveDraft, complete, reset,
  ]);
}